import type { EditRecord } from '../api';
import { ar, arDateTime } from '../ar';
import { IconArrowRight, IconUser } from './icons';

/**
 * Correction history as a Linear-style event timeline.
 *
 * The point is legibility of *change*, not of records: each entry shows the fields that
 * actually moved, before → after, and stays silent about the ones that did not.
 */

interface DiffLine {
  label: string;
  before: string;
  after: string;
  numeric?: boolean;
}

function itemsSummary(state: EditRecord['before']): string {
  if (!state) return '—';
  if (state.itemsComplete) return ar.itemsComplete;
  const items = [...(state.missingItems ?? [])];
  if (state.otherMissingItem) items.push(`${ar.otherItem}: ${state.otherMissingItem}`);
  return items.length > 0 ? items.join('، ') : '—';
}

export function diffLines(edit: EditRecord): DiffLine[] {
  const lines: DiffLine[] = [];
  const before = edit.before;
  const after = edit.after;
  if (!before || !after) return lines;

  if (before.roomNumber !== undefined && before.roomNumber !== after.roomNumber) {
    lines.push({
      label: ar.roomNumber,
      before: before.roomNumber,
      after: after.roomNumber ?? '—',
      numeric: true,
    });
  }

  if (before.inspectionType !== undefined && before.inspectionType !== after.inspectionType) {
    const name = (value?: string) => (value === 'CHECKOUT' ? ar.checkout : ar.clean);
    lines.push({
      label: ar.inspectionType,
      before: name(before.inspectionType),
      after: name(after.inspectionType),
    });
  }

  const beforeItems = itemsSummary(before);
  const afterItems = itemsSummary(after);
  if (beforeItems !== afterItems) {
    lines.push({ label: ar.roomItems, before: beforeItems, after: afterItems });
  }

  return lines;
}

export function AuditTimeline({
  edits,
  showRoom = false,
}: {
  edits: (EditRecord & { roomNumber?: string; inspectionId?: string })[];
  showRoom?: boolean;
  onOpen?: (inspectionId: string) => void;
}) {
  return (
    <ol className="timeline">
      {edits.map((edit, index) => {
        const lines = diffLines(edit);
        return (
          <li key={edit.id} className="timeline-item">
            <span className="timeline-rail" aria-hidden="true">
              <span className="timeline-node" />
            </span>

            <div className="timeline-body">
              <div className="timeline-head">
                <span className="timeline-index num" dir="ltr">
                  {index + 1}
                </span>
                <span className="timeline-actor">
                  <IconUser size={13} />
                  {edit.editorName ?? '—'}
                </span>
                <span className="timeline-action">{ar.auditActionEdited}</span>
                {showRoom && edit.roomNumber && (
                  <span className="timeline-entity num" dir="ltr">
                    {ar.room} {edit.roomNumber}
                  </span>
                )}
                <time className="timeline-time num" dateTime={edit.occurredAt}>
                  {arDateTime(edit.occurredAt)}
                </time>
              </div>

              {lines.length > 0 ? (
                <ul className="diff-list">
                  {lines.map((line) => (
                    <li key={line.label} className="diff-line">
                      <span className="diff-field">{line.label}</span>
                      <span className={`diff-before${line.numeric ? ' num' : ''}`} dir="auto">
                        {line.before}
                      </span>
                      <IconArrowRight size={13} className="diff-arrow flip-rtl" />
                      <span className={`diff-after${line.numeric ? ' num' : ''}`} dir="auto">
                        {line.after}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="diff-empty">{ar.auditNoFieldChange}</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
